import React, { forwardRef, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSwipeable } from "react-swipeable";
import cn from "helpers/classnames";

const tabShape = {
  buttonText: PropTypes.string.isRequired,
  component: PropTypes.element.isRequired,
  key: PropTypes.string.isRequired
};

const TabButton = forwardRef(({ className, isSelected, onClick, tab }, ref) => {
  return (
    <button
      ref={ref}
      id={tab.buttonText}
      type="button"
      className={cn("tab-button", { selected: isSelected }, className)}
      onClick={onClick}
      tabIndex={isSelected ? -1 : 0}
    >
      <span>{tab.buttonText}</span>
    </button>
  );
});

TabButton.propTypes = {
  className: PropTypes.string,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  tab: PropTypes.shape(tabShape).isRequired
};

const Tabs = ({
  className = "",
  onClick,
  selectedTab: tabFromProps,
  shouldAnimateTransitions = false,
  tabButtonClass = "",
  tabContentClass = "",
  tabHolderClass = "",
  tabs
}) => {
  const [selectedTab, setSelectedTab] = useState(tabFromProps || tabs[0]);
  const [pendingTab, setPendingTab] = useState(null);
  const [transitionClass, setTransitionClass] = useState("");
  const tabRefs = useRef([]);
  const currentTabIndex = tabs.findIndex((t) => t.key === selectedTab.key);

  const changeTab = (tab) => {
    if (selectedTab.key === tab.key) {
      return;
    }

    onClick && onClick(tab);

    if (shouldAnimateTransitions) {
      const newTabIndex = tabs.findIndex((t) => t.key === tab.key);
      const animateOutClass =
        newTabIndex > currentTabIndex
          ? "tw-animate-slide-out-to-left"
          : "tw-animate-slide-out-to-right";
      setTransitionClass(animateOutClass); // Start exit animation
      setPendingTab(tab); // Store new tab for switching later
    } else {
      setSelectedTab(tab);
    }
  };

  const handleAnimationEnd = () => {
    if (!shouldAnimateTransitions || !pendingTab) {
      return;
    }

    const animateInClass =
      transitionClass === "tw-animate-slide-out-to-left"
        ? "tw-animate-slide-in-from-right"
        : "tw-animate-slide-in-from-left";

    setSelectedTab(pendingTab); // Switches to tab AFTER exit animation completes
    setTransitionClass(animateInClass); // Start entry animation
    setPendingTab(null); // Reset pending tab
  };

  const handleSwipe = (step) => {
    const nextTab = tabs[currentTabIndex + step];

    changeTab(nextTab);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleSwipe(1),
    onSwipedRight: () => handleSwipe(-1),
    preventScrollOnSwipe: true,
    swipeDuration: 250 //ms
  });

  useEffect(() => {
    if (!pendingTab) {
      setTransitionClass("");
      return;
    }

    const pendingTabIndex = tabs.findIndex((t) => t.key === pendingTab.key);

    if (tabRefs.current[pendingTabIndex]) {
      tabRefs.current[pendingTabIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      });
    }
  }, [pendingTab]);

  return (
    <div className={className}>
      <div className={cn("tw-flex tw-items-end tw-h-12", tabHolderClass)}>
        {tabs.map((tab, index) => (
          <TabButton
            key={tab.key}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            className={tabButtonClass}
            isSelected={selectedTab.buttonText === tab.buttonText}
            onClick={() => changeTab(tab)}
            tab={tab}
          />
        ))}
      </div>
      <div className={tabContentClass}>
        <div
          className={transitionClass}
          data-testid="tab-content"
          key={`${selectedTab.key}-${pendingTab?.key}`}
          onAnimationEnd={handleAnimationEnd}
          {...swipeHandlers}
        >
          {selectedTab.component}
        </div>
      </div>
    </div>
  );
};

Tabs.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  selectedTab: PropTypes.shape(tabShape),
  shouldAnimateTransitions: PropTypes.bool,
  tabButtonClass: PropTypes.string,
  tabContentClass: PropTypes.string,
  tabHolderClass: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.shape(tabShape)).isRequired
};
Tabs.defaultProps = {
  className: "",
  onClick: () => {},
  selectedTab: null,
  shouldAnimateTransitions: false,
  tabButtonClass: "",
  tabContentClass: "",
  tabHolderClass: ""
};

export default Tabs;
